import type {
  CoordePreviewRequest,
  CoordeRun,
  CoordeSetApprovalRequest,
} from '@cortex/domain-ac-mens-hub-api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { rpcClient } from '../lib/orpc.js';
import { queryKeys } from '../lib/query-keys.js';

/**
 * コーデ生成ランの取得・依頼・判定。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Styling, Marketing
 * @graph-business Drives the request → wait → judge loop of outfit generation from the Hub.
 * @graph-connects rpcClient [calls] coorde 手続きの呼び出し
 */

/**
 * 生成中のランを取りに行く間隔（ミリ秒）。
 *
 * 1本あたり十数秒かかり、キューは同時実行1で逐次進むため、5秒ごとに見れば
 * 「1本終わるたびに気づく」粒度になる。これより短くしても増えるのは通信だけ。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling, Marketing
 * @graph-business Sets how often the screen re-checks a run in progress, matched to how fast outfits actually finish.
 * @graph-connects none
 */
export const COORDE_RUN_POLL_INTERVAL_MS = 5000;

/**
 * まだ動いているランかどうか。動いている間だけ取り直す。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling, Marketing
 * @graph-business Tells a finished run from one still producing images, so polling stops as soon as there is nothing left to wait for.
 * @graph-connects none
 */
export function isRunInProgress(run: CoordeRun | undefined): boolean {
  return run?.status === 'queued' || run?.status === 'running';
}

/**
 * 履歴一覧。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling, Marketing
 * @graph-business Lists past generation runs so someone can return to a run they asked for earlier.
 * @graph-connects rpcClient [calls] oRPC coorde.listRuns 呼び出し
 */
export function useCoordeRunList() {
  return useQuery({
    queryKey: queryKeys.coorde.runs,
    queryFn: () => rpcClient.coorde.listRuns(),
    retry: false,
  });
}

/**
 * ラン1件。生成が進んでいる間だけ定期的に取り直す。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling, Marketing
 * @graph-business Follows one run's progress, refreshing while images are still being produced and stopping once everything has settled.
 * @graph-connects rpcClient [calls] oRPC coorde.getRun 呼び出し
 */
export function useCoordeRun(runId: string) {
  return useQuery({
    queryKey: queryKeys.coorde.run(runId),
    queryFn: () => rpcClient.coorde.getRun({ runId }),
    refetchInterval: (query) =>
      isRunInProgress(query.state.data) ? COORDE_RUN_POLL_INTERVAL_MS : false,
    retry: false,
  });
}

/**
 * コーデ案の事前確認。画像は生成しないので費用は発生しない。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling, Marketing
 * @graph-business Shows which garments would be used before any paid image generation starts.
 * @graph-connects rpcClient [calls] oRPC coorde.preview 呼び出し
 */
export function useCoordePreview() {
  return useMutation({
    mutationFn: (input: CoordePreviewRequest) => rpcClient.coorde.preview(input),
  });
}

/**
 * 生成の依頼。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling, Marketing
 * @graph-business Starts a generation run and refreshes the history so the new run appears immediately.
 * @graph-connects rpcClient [calls] oRPC coorde.requestRun 呼び出し
 */
export function useRequestCoordeRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CoordePreviewRequest) => rpcClient.coorde.requestRun(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.coorde.runs });
    },
  });
}

/**
 * 生成物への判定。
 *
 * 手続きが判定後のラン全体を返すので、取り直さずキャッシュを差し替える。判定は
 * 1画面で何度も押すため、そのたびに再取得すると画像の再描画でちらつく。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling, Marketing
 * @graph-business Records a human verdict and updates the screen in place, so judging many outfits in a row stays smooth.
 * @graph-connects rpcClient [calls] oRPC coorde.setApproval 呼び出し
 */
export function useSetCoordeApproval() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CoordeSetApprovalRequest) => rpcClient.coorde.setApproval(input),
    onSuccess: (run) => {
      queryClient.setQueryData(queryKeys.coorde.run(run.runId), run);
      void queryClient.invalidateQueries({ queryKey: queryKeys.coorde.runs });
    },
  });
}
